import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { buttonVariants } from '@/components/ui/button'
import { contactInfo, mainNav } from '@/constants/site'

const isLinkActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    if (href === '/products') return pathname.startsWith('/products') || pathname.startsWith('/collections')

    return pathname.startsWith(href)
}

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 12)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        const closeMenu = () => setIsMenuOpen(false)

        router.events.on('routeChangeStart', closeMenu)

        return () => router.events.off('routeChangeStart', closeMenu)
    }, [router.events])

    useEffect(() => {
        if (!isMenuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsMenuOpen(false)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isMenuOpen])

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b transition-[background-color,border-color,box-shadow] duration-300',
                isScrolled || isMenuOpen
                    ? 'border-border/80 bg-background/90 shadow-[0_1px_12px_-6px_oklch(0.25_0.02_60/0.18)] backdrop-blur-md'
                    : 'border-transparent bg-background'
            )}
        >
            <Container className='flex h-20 items-center justify-between gap-6'>
                <Logo />

                <nav aria-label='منوی اصلی' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNav.map((link) => {
                            const isActive = isLinkActive(router.pathname, link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={isActive ? 'page' : undefined}
                                        className={cn(
                                            'rounded-full px-4 py-2 text-[0.95rem] font-medium transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                                            isActive ? 'bg-secondary text-foreground' : 'text-muted-foreground'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href={contactInfo.phoneHref}
                        className='hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground xl:inline-flex'
                    >
                        <Phone className='size-4' aria-hidden='true' />
                        <span dir='ltr'>
                            {contactInfo.phone}
                        </span>
                    </a>

                    <Link href='/contact' className={cn(buttonVariants({ size: 'sm' }), 'hidden sm:inline-flex')}>
                        مشاوره رایگان
                    </Link>

                    <button
                        type='button'
                        onClick={() => setIsMenuOpen((open) => !open)}
                        aria-expanded={isMenuOpen}
                        aria-controls='mobile-menu'
                        aria-label={isMenuOpen ? 'بستن منو' : 'باز کردن منو'}
                        className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }), 'lg:hidden')}
                    >
                        {isMenuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </Container>

            {isMenuOpen && (
                <div id='mobile-menu' className='border-t border-border bg-background animate-in fade-in slide-in-from-top-2 duration-200 lg:hidden'>
                    <Container className='py-4'>
                        <nav aria-label='منوی موبایل'>
                            <ul className='flex flex-col gap-1'>
                                {mainNav.map((link) => {
                                    const isActive = isLinkActive(router.pathname, link.href)

                                    return (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                aria-current={isActive ? 'page' : undefined}
                                                className={cn(
                                                    'flex items-center rounded-xl px-4 py-3 text-base font-medium transition-colors',
                                                    isActive ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                                                )}
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </nav>

                        <div className='mt-4 grid gap-2 border-t border-border pt-4 sm:grid-cols-2'>
                            <a href={contactInfo.phoneHref} className={cn(buttonVariants({ variant: 'outline' }), 'w-full')}>
                                <Phone aria-hidden='true' />
                                <span dir='ltr'>
                                    {contactInfo.phone}
                                </span>
                            </a>

                            <Link href='/contact' className={cn(buttonVariants(), 'w-full')}>
                                مشاوره رایگان
                            </Link>
                        </div>
                    </Container>
                </div>
            )}
        </header>
    )
}
